import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, Calendar, Play, Clock, LogOut, Sparkles, Send, ThumbsUp, CornerDownRight, Eye, EyeOff, CreditCard, Search } from "lucide-react";
import { formatApiError } from "@/lib/api";

const LOGO = "/upwardcast-logo-transparent.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "uc_attendee_token";

const acc = axios.create({ baseURL: API });
acc.interceptors.request.use((c) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

function fmtDate(iso) {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function fmtPrice(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: (currency || "usd").toUpperCase() }).format(amount);
  } catch {
    return `$${Number(amount || 0).toFixed(0)}`;
  }
}

const modeLabel = (mode) => (mode === "live" ? "Live gathering" : "Anytime experience");

const STATUS = {
  upcoming: { label: "Upcoming", cls: "bg-[#F1EEFB] text-[#7B2CBF]" },
  live: { label: "Live now", cls: "bg-[#16B6A4] text-white" },
  replay: { label: "Replay ready", cls: "bg-[#7B2CBF] text-white" },
  ended: { label: "Ended", cls: "bg-[#E5E0F5] text-[#6A6588]" },
  payment_pending: { label: "Payment pending", cls: "bg-[#FCE9D6] text-[#B5641E]" },
};

export default function Account() {
  const [attendee, setAttendee] = useState(null); // null=checking, false=guest, obj=signed in
  const [items, setItems] = useState(null);
  const [view, setView] = useState("experiences");
  const [expQuery, setExpQuery] = useState("");
  const [expFilter, setExpFilter] = useState("all");

  const loadExperiences = useCallback(() => {
    acc.get("/account/experiences").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) { setAttendee(false); return; }
    acc.get("/account/me")
      .then((r) => { setAttendee(r.data); loadExperiences(); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAttendee(false); });
  }, [loadExperiences]);

  const onAuthed = (data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    setAttendee(data.attendee);
    loadExperiences();
  };
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAttendee(false); setItems(null); };
  const updateAttendee = (a) => setAttendee((prev) => ({ ...prev, ...a }));

  const completePayment = async (it) => {
    try {
      const { data } = await acc.post(`/public/webinars/${it.slug}/checkout`, { registrant_id: it.registrant_id, origin: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout. Please try again.");
    }
  };

  const resetToken = new URLSearchParams(window.location.search).get("token");

  if (attendee === null)
    return (
      <div className="wellness min-h-screen bg-[#FBFAFF] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#7B2CBF]" />
      </div>
    );

  return (
    <div className="wellness min-h-screen bg-[#FBFAFF]" data-testid="account-page">
      <header className="border-b border-[#E5E0F5]">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center"><img src={LOGO} alt="UpwardCast" className="h-8 w-auto" /></Link>
          {attendee && (
            <button onClick={logout} data-testid="account-logout-btn" className="text-sm text-[#6A6588] hover:text-[#7B2CBF] flex items-center gap-1.5 font-medium">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          )}
        </div>
      </header>

      {resetToken && !attendee ? (
        <ResetForm token={resetToken} onAuthed={onAuthed} />
      ) : !attendee ? (
        <AuthForm onAuthed={onAuthed} />
      ) : (
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="animate-fade-up mb-8">
            <div className="text-xs uppercase tracking-[0.25em] text-[#16B6A4] mb-3">Your space</div>
            <h1 className="text-4xl sm:text-5xl font-light leading-tight">Welcome back{attendee.name ? `, ${attendee.name.split(" ")[0]}` : ""}.</h1>
            <p className="text-lg text-[#6A6588] mt-3">Your gatherings, replays and community, all in one calm place.</p>
          </div>

          <div className="flex gap-2 mb-8 border-b border-[#E5E0F5]">
            <button onClick={() => setView("experiences")} data-testid="tab-experiences" className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${view === "experiences" ? "border-[#7B2CBF] text-[#7B2CBF]" : "border-transparent text-[#6A6588] hover:text-[#211C36]"}`}>My experiences</button>
            <button onClick={() => setView("browse")} data-testid="tab-browse" className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${view === "browse" ? "border-[#7B2CBF] text-[#7B2CBF]" : "border-transparent text-[#6A6588] hover:text-[#211C36]"}`}>Browse experiences</button>
            <button onClick={() => setView("community")} data-testid="tab-community" className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${view === "community" ? "border-[#7B2CBF] text-[#7B2CBF]" : "border-transparent text-[#6A6588] hover:text-[#211C36]"}`}>Community</button>
            <button onClick={() => setView("settings")} data-testid="tab-settings" className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${view === "settings" ? "border-[#7B2CBF] text-[#7B2CBF]" : "border-transparent text-[#6A6588] hover:text-[#211C36]"}`}>Settings</button>
          </div>

          {view === "community" ? (
            <CommunityFeed acc={acc} experiences={items || []} />
          ) : view === "settings" ? (
            <AccountSettings attendee={attendee} onUpdated={updateAttendee} />
          ) : view === "browse" ? (
            <BrowseExperiences />
          ) : items === null ? (
            <div className="text-[#9A95B5] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-[2rem] soft-shadow p-12 text-center" data-testid="account-empty">
              <Sparkles className="w-7 h-7 text-[#7B2CBF] mx-auto mb-4" />
              <p className="text-xl font-light mb-2">Nothing here just yet.</p>
              <p className="text-[#6A6588] mb-6">When you reserve a spot, it will appear here with your replay.</p>
              <Link to="/" className="btn-gradient text-white px-6 py-3 rounded-full font-medium inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform">
                Explore experiences <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-[#9A95B5] absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    value={expQuery}
                    onChange={(e) => setExpQuery(e.target.value)}
                    placeholder="Search your experiences"
                    data-testid="exp-search"
                    className="w-full bg-white border border-[#E5E0F5] rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]"
                  />
                </div>
                <select
                  value={expFilter}
                  onChange={(e) => setExpFilter(e.target.value)}
                  data-testid="exp-filter"
                  className="bg-white border border-[#E5E0F5] rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]"
                >
                  <option value="all">All statuses</option>
                  <option value="live">Live now</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="replay">Replay ready</option>
                  <option value="payment_pending">Payment pending</option>
                </select>
              </div>
              {(() => {
                const filtered = items.filter((it) => {
                  const matchesQ = !expQuery.trim() || (it.title || "").toLowerCase().includes(expQuery.trim().toLowerCase());
                  const matchesF = expFilter === "all" || it.status === expFilter;
                  return matchesQ && matchesF;
                });
                if (filtered.length === 0) {
                  return <div className="bg-white rounded-[2rem] soft-shadow p-10 text-center text-[#6A6588]" data-testid="exp-no-results">No experiences match your search.</div>;
                }
                return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filtered.map((it) => {
                const st = STATUS[it.status] || STATUS.ended;
                return (
                  <div key={it.registrant_id} className="bg-white rounded-[2rem] soft-shadow overflow-hidden animate-fade-up" data-testid={`account-exp-${it.registrant_id}`}>
                    <div className="h-40 bg-[#F1EEFB] overflow-hidden">
                      {it.image_url ? <img src={it.image_url} alt={it.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Play className="w-8 h-8 text-[#C9C2E8]" /></div>}
                    </div>
                    <div className="p-6">
                      <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3 ${st.cls}`} data-testid={`account-status-${it.registrant_id}`}>{st.label}</span>
                      <h3 className="text-xl font-light mb-2">{it.title}</h3>
                      <div className="text-sm text-[#6A6588] flex items-center gap-1.5 mb-5">
                        <Calendar className="w-3.5 h-3.5" /> {fmtDate(it.session_start)}
                      </div>
                      {it.can_watch ? (
                        <Link to={`/watch/${it.registrant_id}`} data-testid={`account-watch-${it.registrant_id}`} className="btn-gradient text-white px-5 py-2.5 rounded-full font-medium inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform">
                          {it.status === "replay" ? "Watch replay" : "Enter live room"} <ArrowRight className="w-4 h-4" />
                        </Link>
                      ) : it.status === "payment_pending" ? (
                        <div data-testid={`account-pending-${it.registrant_id}`}>
                          <p className="text-sm text-[#B5641E] mb-3 leading-relaxed">Your spot is reserved, but payment didn't go through. Complete your payment to unlock this experience and its replay.</p>
                          <button onClick={() => completePayment(it)} data-testid={`account-complete-payment-${it.registrant_id}`} className="btn-gradient text-white px-5 py-2.5 rounded-full font-medium inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform">
                            <CreditCard className="w-4 h-4" /> Complete payment{it.price ? ` · ${fmtPrice(it.price, it.currency)}` : ""}
                          </button>
                        </div>
                      ) : it.status === "upcoming" ? (
                        <div>
                          <Link to={`/watch/${it.registrant_id}`} data-testid={`account-enter-${it.registrant_id}`} className="btn-gradient text-white px-5 py-2.5 rounded-full font-medium inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform">
                            Enter room <ArrowRight className="w-4 h-4" />
                          </Link>
                          <div className="text-xs text-[#9A95B5] flex items-center gap-1.5 mt-3"><Clock className="w-3 h-3" /> Opens at your start time, you'll wait in the room until it begins.</div>
                        </div>
                      ) : (
                        <div className="text-sm text-[#9A95B5]">Replay not available for this one.</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
                );
              })()}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder = "Password", testid = "account-password-input" }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input required type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} data-testid={testid}
        className="w-full bg-[#F1EEFB] rounded-2xl px-5 py-3.5 pr-12 text-sm placeholder-[#9A95B5] focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40" />
      <button type="button" onClick={() => setShow(!show)} data-testid={`${testid}-toggle`} aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9A95B5] hover:text-[#7B2CBF]">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function AccountSettings({ attendee, onUpdated }) {
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(attendee?.name || "");
  const [savingName, setSavingName] = useState(false);
  const set = (k, v) => setPw((p) => ({ ...p, [k]: v }));
  const saveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name cannot be empty");
    setSavingName(true);
    try {
      const { data } = await acc.patch("/account/profile", { name: name.trim() });
      onUpdated?.({ name: data.name });
      toast.success("Name updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update name");
    }
    setSavingName(false);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (pw.next.length < 6) return toast.error("New password must be at least 6 characters");
    if (pw.next !== pw.confirm) return toast.error("New passwords don't match");
    setLoading(true);
    try {
      await acc.post("/account/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update password");
    }
    setLoading(false);
  };
  return (
    <div className="max-w-md space-y-6" data-testid="account-settings">
      <div className="bg-white rounded-[2rem] soft-shadow p-8 animate-fade-up">
        <h2 className="text-2xl font-light mb-1">Your details</h2>
        <p className="text-[#6A6588] text-sm mb-6">This is the name shown across your gatherings and community.</p>
        <form onSubmit={saveName} className="space-y-4">
          <div>
            <label className="text-xs text-[#6A6588]">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" placeholder="Your name"
              className="w-full bg-[#FBFAFF] border border-[#E5E0F5] rounded-2xl px-4 py-3 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]" />
          </div>
          <div>
            <label className="text-xs text-[#6A6588]">Email</label>
            <input value={attendee?.email || ""} disabled data-testid="settings-email-display"
              className="w-full bg-[#F1EEFB] border border-[#E5E0F5] rounded-2xl px-4 py-3 text-sm mt-1 text-[#6A6588]" />
          </div>
          <button type="submit" disabled={savingName} data-testid="settings-save-name-btn"
            className="w-full btn-gradient text-white px-6 py-3.5 rounded-2xl font-medium flex items-center justify-center disabled:opacity-50">
            {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save name"}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-[2rem] soft-shadow p-8 animate-fade-up">
        <h2 className="text-2xl font-light mb-1">Change password</h2>
        <p className="text-[#6A6588] text-sm mb-6">Update the password you use to sign in.</p>
        <form onSubmit={submit} className="space-y-4">
          <PasswordInput value={pw.current} onChange={(e) => set("current", e.target.value)} placeholder="Current password" testid="settings-current-password" />
          <PasswordInput value={pw.next} onChange={(e) => set("next", e.target.value)} placeholder="New password" testid="settings-new-password" />
          <PasswordInput value={pw.confirm} onChange={(e) => set("confirm", e.target.value)} placeholder="Confirm new password" testid="settings-confirm-password" />
          <button type="submit" disabled={loading} data-testid="settings-change-password-btn"
            className="w-full btn-gradient text-white px-6 py-3.5 rounded-2xl font-medium flex items-center justify-center disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}


function AuthForm({ onAuthed }) {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await acc.post("/account/forgot-password", { email: f.email, origin: window.location.origin });
        setSentTo(f.email);
        setLoading(false);
        return;
      }
      const { data } = await acc.post(`/account/${mode === "login" ? "login" : "register"}`, { ...f, origin: window.location.origin });
      onAuthed(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
      setLoading(false);
    }
  };

  if (sentTo) {
    return (
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="bg-white rounded-[2rem] soft-shadow p-8 animate-fade-up text-center" data-testid="forgot-sent">
          <Sparkles className="w-7 h-7 text-[#7B2CBF] mx-auto mb-4" />
          <h1 className="text-2xl font-light mb-2">Check your inbox</h1>
          <p className="text-[#6A6588] text-sm mb-6">If an account exists for <span className="text-[#211C36]">{sentTo}</span>, we just sent a link to reset your password. It is good for one hour.</p>
          <button onClick={() => { setSentTo(""); setMode("login"); }} className="text-sm text-[#7B2CBF] font-medium">Back to sign in</button>
        </div>
      </div>
    );
  }

  const titles = {
    login: ["Welcome back", "Sign in to see your gatherings and replays."],
    register: ["Create your space", "So you can return to your experiences anytime."],
    forgot: ["Reset your password", "Enter your email and we will send you a reset link."],
  };
  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="bg-white rounded-[2rem] soft-shadow p-8 animate-fade-up">
        <h1 className="text-3xl font-light mb-1">{titles[mode][0]}</h1>
        <p className="text-[#6A6588] mb-7 text-sm">{titles[mode][1]}</p>
        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" data-testid="account-name-input"
              className="w-full bg-[#F1EEFB] rounded-2xl px-5 py-3.5 text-sm placeholder-[#9A95B5] focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40" />
          )}
          <input required type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email address" data-testid="account-email-input"
            className="w-full bg-[#F1EEFB] rounded-2xl px-5 py-3.5 text-sm placeholder-[#9A95B5] focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40" />
          {mode !== "forgot" && <PasswordInput value={f.password} onChange={(e) => set("password", e.target.value)} />}
          {mode === "login" && (
            <button type="button" onClick={() => setMode("forgot")} data-testid="forgot-password-link" className="text-sm text-[#7B2CBF] font-medium">
              Forgot password?
            </button>
          )}
          <button type="submit" disabled={loading} data-testid="account-submit-btn"
            className="w-full btn-gradient text-white disabled:opacity-50 px-6 py-4 rounded-full font-medium flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link")}
          </button>
        </form>
        <button onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="account-toggle-mode"
          className="w-full text-center text-sm text-[#7B2CBF] mt-5 font-medium">
          {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
        </button>
      </div>
    </div>
  );
}

function ResetForm({ token, onAuthed }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await acc.post("/account/reset-password", { token, password });
      toast.success("Password updated, you are signed in");
      window.history.replaceState({}, "", "/account");
      onAuthed(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "This link is invalid or expired");
      setLoading(false);
    }
  };
  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="bg-white rounded-[2rem] soft-shadow p-8 animate-fade-up" data-testid="reset-form">
        <h1 className="text-3xl font-light mb-1">Choose a new password</h1>
        <p className="text-[#6A6588] mb-7 text-sm">Almost there. Pick something you will remember.</p>
        <form onSubmit={submit} className="space-y-4">
          <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" testid="reset-password-input" />
          <button type="submit" disabled={loading} data-testid="reset-submit-btn"
            className="w-full btn-gradient text-white disabled:opacity-50 px-6 py-4 rounded-full font-medium flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
          </button>
        </form>
        <Link to="/account" onClick={() => window.history.replaceState({}, "", "/account")} className="block text-center text-sm text-[#7B2CBF] mt-5 font-medium">Back to sign in</Link>
      </div>
    </div>
  );
}

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Avatar({ name, url, ai }) {
  return (
    <div className="w-10 h-10 rounded-full bg-[#E5E0F5] overflow-hidden flex items-center justify-center font-display text-[#7B2CBF] shrink-0">
      {url ? <img src={url} alt={name} className="w-full h-full object-cover" /> : (name?.[0]?.toUpperCase() || "?")}
    </div>
  );
}

function Reply({ post, onResonate }) {
  return (
    <div data-testid={`community-post-${post.id}`}>
      <div className="flex items-start gap-3">
        <Avatar name={post.author_name} url={post.author_avatar} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-[#211C36]">{post.author_name}</span>
            {(post.author_type === "host" || post.author_type === "admin") && <span className="text-[10px] uppercase tracking-wider bg-[#7B2CBF] text-white px-1.5 py-0.5 rounded">Guide</span>}
            <span className="text-xs text-[#9A95B5]">{timeAgo(post.created_at)}</span>
          </div>
          <p className="text-[#3F3A56] mt-2 leading-relaxed whitespace-pre-wrap">{post.body}</p>
          <button onClick={() => onResonate(post)} data-testid={`resonate-btn-${post.id}`}
            className={`flex items-center gap-1.5 text-sm mt-2 transition-colors ${post.you_resonated ? "text-[#7B2CBF] font-medium" : "text-[#9A95B5] hover:text-[#7B2CBF]"}`}>
            <ThumbsUp className="w-4 h-4" /> {post.resonates > 0 ? post.resonates : ""} resonate{post.resonates === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Post({ post, onResonate, onReply }) {
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    await onReply(post.id, text.trim());
    setText(""); setReplying(false); setBusy(false);
  };
  return (
    <div className="bg-white rounded-3xl soft-shadow p-6 animate-fade-up" data-testid={`community-post-${post.id}`}>
      <div className="flex items-start gap-3">
        <Avatar name={post.author_name} url={post.author_avatar} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-[#211C36]">{post.author_name}</span>
            {(post.author_type === "host" || post.author_type === "admin") && <span className="text-[10px] uppercase tracking-wider bg-[#7B2CBF] text-white px-1.5 py-0.5 rounded">Guide</span>}
            <span className="text-xs text-[#9A95B5]">{timeAgo(post.created_at)}</span>
          </div>
          {post.experience_title && (
            post.experience_slug
              ? <Link to={`/w/${post.experience_slug}`} data-testid={`post-experience-link-${post.id}`} className="text-xs text-[#16B6A4] hover:text-[#7B2CBF] hover:underline mt-0.5 inline-flex items-center gap-1 transition-colors">on {post.experience_title} <ArrowRight className="w-3 h-3" /></Link>
              : <div className="text-xs text-[#16B6A4] mt-0.5">on {post.experience_title}</div>
          )}
          <p className="text-[#3F3A56] mt-2 leading-relaxed whitespace-pre-wrap">{post.body}</p>
          <div className="flex items-center gap-4 mt-3">
            <button onClick={() => onResonate(post)} data-testid={`resonate-btn-${post.id}`}
              className={`flex items-center gap-1.5 text-sm transition-colors ${post.you_resonated ? "text-[#7B2CBF] font-medium" : "text-[#9A95B5] hover:text-[#7B2CBF]"}`}>
              <ThumbsUp className="w-4 h-4" /> {post.resonates > 0 ? post.resonates : ""} resonate{post.resonates === 1 ? "" : "s"}
            </button>
            <button onClick={() => setReplying(!replying)} data-testid={`reply-toggle-${post.id}`} className="flex items-center gap-1.5 text-sm text-[#9A95B5] hover:text-[#7B2CBF]">
              <CornerDownRight className="w-4 h-4" /> Reply
            </button>
          </div>
          {replying && (
            <div className="flex gap-2 mt-3">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Share a kind word…" data-testid={`reply-input-${post.id}`}
                className="flex-1 bg-[#F1EEFB] rounded-full px-4 py-2 text-sm placeholder-[#9A95B5] focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40" />
              <button onClick={submit} disabled={busy} data-testid={`reply-submit-${post.id}`} className="btn-gradient text-white px-4 rounded-full text-sm font-medium disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          )}
          {post.replies && post.replies.length > 0 && (
            <div className="mt-4 space-y-4 pl-4 border-l-2 border-[#F1EEFB]">
              {post.replies.map((r) => <Reply key={r.id} post={r} onResonate={onResonate} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BrowseExperiences() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [hostFilter, setHostFilter] = useState("");
  const [sort, setSort] = useState("default");
  useEffect(() => {
    acc.get("/public/webinars").then((r) => setList(r.data)).catch(() => setList([]));
  }, []);
  const allHosts = Array.from(
    new Set((list || []).flatMap((w) => (w.hosts || []).map((h) => (h.name || "").trim()).filter(Boolean)))
  ).sort((a, b) => a.localeCompare(b));
  const s = q.trim().toLowerCase();
  let filtered = (list || []).filter((w) => {
    const hostNames = (w.hosts || []).map((h) => h.name || "");
    if (hostFilter && !hostNames.includes(hostFilter)) return false;
    if (!s) return true;
    return `${w.title} ${w.description} ${hostNames.join(" ")}`.toLowerCase().includes(s);
  });
  if (sort === "price-asc") filtered = [...filtered].sort((a, b) => (a.price || 0) - (b.price || 0));
  else if (sort === "price-desc") filtered = [...filtered].sort((a, b) => (b.price || 0) - (a.price || 0));
  return (
    <div data-testid="browse-experiences">
      <div className="flex flex-wrap items-center gap-3 mb-7">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-[#9A95B5] absolute left-4 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search experiences, guides, topics…" data-testid="browse-search-input"
            className="w-full bg-white border border-[#E5E0F5] rounded-full pl-11 pr-4 py-3 text-sm placeholder-[#9A95B5] focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40" />
        </div>
        <select value={hostFilter} onChange={(e) => setHostFilter(e.target.value)} data-testid="browse-host-filter" className="bg-white border border-[#E5E0F5] rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40">
          <option value="">All hosts</option>
          {allHosts.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="browse-sort" className="bg-white border border-[#E5E0F5] rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40">
          <option value="default">Featured</option>
          <option value="price-asc">Price: low to high</option>
          <option value="price-desc">Price: high to low</option>
        </select>
      </div>
      {list === null ? (
        <div className="text-[#9A95B5] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-[2rem] soft-shadow p-12 text-center text-[#6A6588]" data-testid="browse-empty">
          {s ? "No experiences match your search." : "New experiences are being prepared with care. Check back soon."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((w) => (
            <a key={w.id} href={`/w/${w.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`browse-card-${w.slug}`}
              className="group bg-white rounded-[1.75rem] overflow-hidden soft-shadow-sm transition-all duration-500 hover:-translate-y-1.5 flex flex-col">
              <div className="relative h-40 overflow-hidden bg-[#E5E0F5]">
                {w.image_url
                  ? <img src={w.image_url} alt={w.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  : <div className="w-full h-full flex items-center justify-center"><Play className="w-8 h-8 text-[#C9C2E8]" /></div>}
                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-[#7B2CBF]">{modeLabel(w.mode)}</div>
                <div className="absolute top-3 right-3 btn-gradient text-white px-3 py-1 rounded-full text-xs font-semibold">{w.price > 0 ? fmtPrice(w.price, w.currency) : "Free"}</div>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-lg font-light mb-2 leading-snug">{w.title}</h3>
                <p className="text-sm text-[#6A6588] leading-relaxed line-clamp-2 flex-1">{w.description}</p>
                <div className="mt-4 flex items-center gap-2 text-[#7B2CBF] font-medium text-sm">
                  Reserve your spot <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function CommunityFeed({ acc, experiences }) {
  const [posts, setPosts] = useState(null);
  const [options, setOptions] = useState([]);
  const [body, setBody] = useState("");
  const [expId, setExpId] = useState("");
  const [filter, setFilter] = useState("");
  const [posting, setPosting] = useState(false);
  const load = useCallback(() => {
    const qs = filter ? `?experience_id=${filter}` : "";
    acc.get(`/account/community${qs}`).then((r) => {
      setPosts(r.data);
      if (!filter) {
        const seen = {};
        r.data.forEach((p) => { if (p.experience_id && p.experience_title) seen[p.experience_id] = p.experience_title; });
        setOptions(Object.entries(seen).map(([id, title]) => ({ id, title })));
      }
    }).catch(() => setPosts([]));
  }, [acc, filter]);
  useEffect(() => { load(); }, [load]);

  const createPost = async () => {
    if (!body.trim()) return;
    setPosting(true);
    try {
      await acc.post("/account/community", { body: body.trim(), experience_id: expId || undefined });
      setBody(""); setExpId(""); load();
      toast.success("Shared with the community");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    setPosting(false);
  };
  const onResonate = async (post) => {
    try {
      const { data } = await acc.post(`/account/community/${post.id}/resonate`);
      setPosts((ps) => ps.map((p) => updateRes(p, post.id, data)));
    } catch { /* ignore */ }
  };
  const updateRes = (p, id, data) => {
    if (p.id === id) return { ...p, resonates: data.resonates, you_resonated: data.you_resonated };
    return { ...p, replies: (p.replies || []).map((r) => r.id === id ? { ...r, resonates: data.resonates, you_resonated: data.you_resonated } : r) };
  };
  const onReply = async (postId, text) => {
    try { await acc.post(`/account/community/${postId}/reply`, { body: text }); load(); } catch (err) { toast.error("Failed"); }
  };

  return (
    <div data-testid="community-feed">
      <div className="bg-white rounded-3xl soft-shadow p-6 mb-6">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Share a reflection, an intention, or a moment of gratitude…" data-testid="community-compose-input"
          className="w-full bg-[#F1EEFB] rounded-2xl px-4 py-3 text-sm placeholder-[#9A95B5] focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40" />
        <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
          {experiences.length > 0 ? (
            <select value={expId} onChange={(e) => setExpId(e.target.value)} data-testid="community-compose-exp"
              className="bg-[#F1EEFB] rounded-full px-4 py-2 text-sm text-[#6A6588] focus:outline-none">
              <option value="">Not about a specific experience</option>
              {experiences.map((e) => <option key={e.registrant_id} value={e.experience_id}>{e.title}</option>)}
            </select>
          ) : <span />}
          <button onClick={createPost} disabled={posting || !body.trim()} data-testid="community-post-btn"
            className="btn-gradient text-white px-5 py-2.5 rounded-full font-medium flex items-center gap-2 disabled:opacity-50 hover:-translate-y-0.5 transition-transform">
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Share
          </button>
        </div>
      </div>
      {posts === null ? (
        <div className="text-[#9A95B5] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          {options.length > 0 && (
            <div className="flex items-center gap-2 mb-5">
              <span className="text-sm text-[#6A6588]">Filter:</span>
              <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="community-filter"
                className="bg-white rounded-full px-4 py-2 text-sm text-[#211C36] border border-[#E5E0F5] focus:outline-none">
                <option value="">All reflections</option>
                {options.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
              </select>
            </div>
          )}
          {posts.length === 0 ? (
            <div className="bg-white rounded-3xl soft-shadow p-12 text-center text-[#6A6588]" data-testid="community-empty">{filter ? "No reflections for this experience yet." : "Be the first to share a reflection."}</div>
          ) : (
            <div className="space-y-5">{posts.map((p) => <Post key={p.id} post={p} onResonate={onResonate} onReply={onReply} />)}</div>
          )}
        </>
      )}
    </div>
  );
}
